import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getBookabilityUnmetCriteriaMetadata } from "@src/appV2/OpenShifts/ShiftAction/getBookabilityUnmetCriteriaMetadata";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { MY_SHIFTS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { invalidateShifts } from "../../invalidateShifts";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { convertShiftToSlotId } from "../Open/useGetShiftsSlotV2";
import { ShiftBottomSheetBookabilityCriteriaCard } from "./BookabilityCriteriaCard";

export function ShiftNotBookableCard() {
  const { bookabilityStatus, shiftFailedLoading, shift, shiftSlots, worker } =
    useShiftModalsDataContext();
  const queryClient = useQueryClient();
  const history = useHistory();
  const shiftSlot =
    shiftSlots?.[
      convertShiftToSlotId({
        start: shift?.attributes.start ?? "",
        end: shift?.attributes.end ?? "",
        facilityId: shift?.workplace.id ?? "",
      })
    ];

  const isShiftAlreadyGrabbed = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_ALREADY_GRABBED,
  ]);

  const isShiftDeleted = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_DELETED,
  ]);

  const isShiftNotAvailable = isShiftAlreadyGrabbed || isShiftDeleted;

  useEffect(() => {
    // We're displaying an outdated open shifts list with unbookable shifts.
    // We need to invalidate the data so that the list is updated.
    void invalidateShifts(queryClient);
  }, [
    queryClient,
    // Adding here to re-run the effect if the shift status changes from available to not available
    isShiftNotAvailable,
  ]);

  if (isShiftAlreadyGrabbed) {
    const shiftAlreadyGrabbedMetadata = getBookabilityUnmetCriteriaMetadata(
      bookabilityStatus!,
      BookabilityUnmetCriteria.SHIFT_ALREADY_GRABBED
    );

    const isShiftBookedByCurrentWorker =
      isDefined(
        shiftSlot?.shiftAssignments.find(
          (shiftAssignment) => shiftAssignment.workerId === worker.userId
        )
      ) || shiftAlreadyGrabbedMetadata?.workerId === worker.userId;

    return isShiftBookedByCurrentWorker ? (
      <ShiftBottomSheetBookabilityCriteriaCard
        intent="success"
        title="You're booked for this shift!"
        illustrationType="shift-booked"
        footer={
          <Button
            size="medium"
            role="button"
            sx={{ width: "100%" }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.TAPPED_VIEW_BOOKINGS_FROM_BOOKABILITY_CARD, {
                shiftId: shift?.id,
                bookabilityMetadataWorkerId: shiftAlreadyGrabbedMetadata?.workerId,
              });
              history.push(MY_SHIFTS_FULL_PATH);
            }}
          >
            View My Bookings
          </Button>
        }
      />
    ) : (
      <ShiftBottomSheetBookabilityCriteriaCard
        intent="error"
        title="Shift already booked"
        description="Apologies for the inconvenience. This shift was already booked by someone else. Check out other shifts in your area."
        illustrationType="error"
      />
    );
  }

  if (isShiftDeleted) {
    return (
      <ShiftBottomSheetBookabilityCriteriaCard
        intent="error"
        title="Shift cancelled"
        description="Apologies for the inconvenience. This shift was cancelled by the workplace and is no longer available."
        illustrationType="error"
      />
    );
  }

  if (shiftFailedLoading) {
    return (
      <ShiftBottomSheetBookabilityCriteriaCard
        intent="error"
        title="Shift unavailable"
        description="Apologies for the inconvenience. This shift is no longer available. Check out other shifts in your area."
        illustrationType="error"
      />
    );
  }

  return null;
}
